import styled from '@emotion/styled';
import { Typography } from '@material-ui/core';
import { ClassAttributes, createElement } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  Creative,
  CreativeFormat,
  CreativeFormatAspectRatio,
  CreativeFormatFastStart,
  CreativeFormatPlaybackTime,
  Deliverable,
  File,
  Maybe,
} from '../../../../graphql/generated/graphql';
import { SPACING_REGULAR, SPACING_UNIT } from '../../../constant/spacing';
import { gridLayoutContainer, gridLayoutItem } from '../../../styleUtility/gridLayout';
import AspectRatioFixed from '../../primitive/AspectRatioFixed';
import _FileSize from '../../primitive/FileSize';
import ImageScreen from '../../primitive/ImageScreen';
import LfToBr from '../../primitive/LfToBr';
import MediaPlayer from '../MediaPlayer';
import CreativeAttributeFileName from './CreativeAttributeFileName';
import _CreativeAttributeTable from './CreativeAttributeTable';

export type DisplayType = 'submission' | 'modification';

export interface Props {
  creativeFormat: {
    creativeType: Pick<CreativeFormat['creativeType'], 'key' | 'name'>;
    attribute: {
      playbackTime?: {
        seconds: CreativeFormatPlaybackTime['seconds'];
      } | null;
      aspectRatio?: Pick<CreativeFormatAspectRatio, 'aspectRatio' | 'size'> | null;
      fastStart?: CreativeFormatFastStart | null;
    };
  };
  deliverable: Pick<Deliverable, 'intent'> & {
    creative: Pick<Creative, 'id'> & {
      file: Pick<File, 'url' | 'mimeType' | 'fastStart' | 'attribute' | 'originalFileName'>;
    };
    attachedThumbnail?: Maybe<{
      file: Pick<File, 'url' | 'size' | 'originalFileName'>;
    }>;
  };
  type: DisplayType;
  className?: string;
}

const DeliverableDetail = ({ creativeFormat, deliverable, type, className }: ClassAttributes<HTMLElement> & Props) => (
  <Root className={className}>
    <VideoOrImage aspectRatio={[4, 3]}>
      <MediaPlayer mimeType={deliverable.creative.file.mimeType} url={deliverable.creative.file.url} />
    </VideoOrImage>

    <CreativeAttribute>
      <CreativeAttributeFileName file={deliverable.creative.file} />
      <CreativeAttributeTable file={deliverable.creative.file} creativeFormat={creativeFormat} />
    </CreativeAttribute>

    {deliverable.attachedThumbnail?.file?.url && (
      <Attachment>
        <AttachmentHeading variant="subtitle1">
          <FormattedMessage id="submission_attachment.type.thumbnail_image" />
        </AttachmentHeading>
        <Typography variant="caption">
          <FormattedMessage id="submission_attachment.type.thumbnail_image.message" />
        </Typography>
        <AttachedThumbnail src={deliverable.attachedThumbnail.file.url} clickable />
        <FileData>
          <Typography variant="caption">{deliverable.attachedThumbnail.file.originalFileName}</Typography>
          <FileSize fileSize={deliverable.attachedThumbnail.file.size}>
            {(fileSize) => <Typography variant="caption">{fileSize}</Typography>}
          </FileSize>
        </FileData>
      </Attachment>
    )}
    <Intent>
      <IntentHeading variant="subtitle1" noWrap>
        <FormattedMessage id={`deliverable.intent_heading_${type}`} />
      </IntentHeading>

      <IntentText variant="body1">
        <LfToBr text={deliverable.intent} />
      </IntentText>
    </Intent>
  </Root>
);

const Root = styled('div')(
  gridLayoutContainer({
    columns: ['1fr', `${SPACING_UNIT * 4}px`, '1fr'],
    rows: ['auto', 'auto', 'auto', 'auto'],
    areas: [
      ['video', '.', 'creativeAttribute'],
      ['attachment', 'attachment', 'attachment'],
      ['intent', 'intent', 'intent'],
      ['submit', 'submit', 'submit'],
    ],
  }),
);

const VideoOrImage = styled(AspectRatioFixed)(gridLayoutItem('video', { alignSelf: 'flex-start' }));

const Intent = styled('div')(gridLayoutItem('intent'), {
  marginTop: SPACING_UNIT * 4,
});

const IntentHeading = styled(Typography)(gridLayoutItem('intentHeading'), { marginBottom: SPACING_UNIT * 2 });

const IntentText = styled(Typography)(gridLayoutItem('intentBody'), {
  wordBreak: 'break-word' as any,
  hyphens: 'auto',
});

const CreativeAttribute = styled('div')(gridLayoutItem('creativeAttribute'), {});

const CreativeAttributeTable = styled(_CreativeAttributeTable)({
  marginTop: SPACING_UNIT,
});

const Attachment = styled('div')(
  gridLayoutContainer({
    columns: ['auto', '1fr'],
    rows: ['auto', 'auto', 'auto', '1fr'],
    areas: [
      ['attachmentHeading', 'attachmentHeading'],
      ['attachedThumbnail', 'attachmentType'],
      ['attachedThumbnail', 'attachmentFileName'],
      ['attachedThumbnail', 'attachmentFileSize'],
    ],
  }),
  gridLayoutItem('attachment'),
  {
    marginTop: SPACING_UNIT * 4,
  },
);

const AttachmentHeading = styled(Typography)(gridLayoutItem('attachmentHeading'), {});

const AttachedThumbnail = styled(ImageScreen)(gridLayoutItem('attachedThumbnail', { alignSelf: 'center' }), {
  marginRight: SPACING_REGULAR,
  maxWidth: 200,
  height: 120,
});

const AttatchmentType = styled(Typography)(gridLayoutItem('attachmentType', { alignSelf: 'end' }));
AttatchmentType.defaultProps = { color: 'textSecondary' };

const FileSize = styled(_FileSize)(gridLayoutItem('attachmentFileSize'));

const FileData = styled('div')({
  flex: 1,
  wordBreak: 'break-word',
  padding: '5px',
});

export default DeliverableDetail;
